import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import Card from '../../../core/layout/Card';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import { get, capitalize } from 'lodash';
import Separator from '../../../core/layout/Separator';
import FormButtons from '../../../core/form/FormButtons';
import Select from '../../../core/form/Select';
import DateInput from '../../../core/form/DateInput';
import FetchSelect from '../../../core/form/FetchSelect';
import LocalFileInput from '../../../core/form/LocalFileInput';
import Button from '../../../core/form/Button';

const TARGET_AUDIENCE = {
  FREE_TRIAL: 'FREE_TRIAL',
  CUSTOMERS: 'CUSTOMERS',
};

const BANNER_STATUS = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
};

const STAGE = {
  ACTIVATION: 'ACTIVATION',
  ADOPTION: 'ADOPTION',
  ONBOARDING: 'ONBOARDING',
  ONGOING: 'ONGOING',
};

const BannerForm = (props) => {
  const isEditing = useMemo(() => {
    return !!get(props.values, 'id', null);
  }, [props.values]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Card
        title={isEditing ? 'Update Banner' : 'Create Banner'}
        actions={<Button icon="arrow-left" label="Back" type="default" onClick={() => browserHistory.goBack()} />}
      >
        <Row>
          <Column size={4}>
            <TextInput
              required
              label="Name"
              value={get(props.values, 'name', '')}
              onChange={(value) => props.onChange('name', value)}
              errorText={get(props.errors, 'name', null)}
              disabled={props.submitting || props.loading}
            />
          </Column>
          <Column size={4}>
            <TextInput
              label="Link"
              value={get(props.values, 'link', '')}
              onChange={(value) => props.onChange('link', value)}
              errorText={get(props.errors, 'link', null)}
              disabled={props.submitting || props.loading}
            />
          </Column>
          <Column size={4}>
            <TextInput
              required
              label="Position"
              value={get(props.values, 'position', null)}
              onChange={(value) => props.onChange('position', value)}
              errorText={get(props.errors, 'position', null)}
              disabled={props.submitting || props.loading}
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            <Select
              label="Target Audience"
              value={get(props.values, 'targetAudience', '')}
              onChange={(value) => props.onChange('targetAudience', value)}
              errorText={get(props.errors, 'targetAudience', null)}
              disabled={props.submitting || props.loading}
              placeholder="All"
              options={[
                {
                  label: 'Customers',
                  value: TARGET_AUDIENCE.CUSTOMERS,
                  disabled: false,
                },
                {
                  label: 'Leads free-trial',
                  value: TARGET_AUDIENCE.FREE_TRIAL,
                  disabled: false,
                },
              ]}
            />
          </Column>
          <Column size={4}>
            <Select
              label="Stage"
              value={get(props.values, 'stage', '')}
              onChange={(value) => props.onChange('stage', value)}
              errorText={get(props.errors, 'stage', null)}
              disabled={props.submitting || props.loading}
              placeholder="All"
              options={Object.values(STAGE).map((value) => ({
                label: capitalize(value),
                value,
                disabled: false,
              }))}
            />
          </Column>
          <Column size={4}>
            <Select
              required
              label="Status"
              value={get(props.values, 'status', '')}
              onChange={(value) => props.onChange('status', value)}
              errorText={get(props.errors, 'status', null)}
              disabled={props.submitting || props.loading}
              options={Object.values(BANNER_STATUS).map((value) => ({
                label: capitalize(value),
                value,
                disabled: false,
              }))}
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            <FetchSelect
              multiple
              showSearch
              required
              url="/segments"
              disabled={props.submitting || props.loading}
              label="Segments"
              value={get(props.values, 'segments', '')}
              errorText={get(props.errors, 'segments', null)}
              onChange={(segment) => props.onChange('segments', segment)}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
          <Column size={4}>
            <DateInput
              utc
              required
              disabled={props.submitting || props.loading}
              label="Initial publication date"
              value={get(props.values, 'initialDate', null)}
              onChange={(value) => props.onChange('initialDate', value)}
              errorText={get(props.errors, 'initialDate', null)}
            />
          </Column>
          <Column size={4}>
            <DateInput
              utc
              required
              disabled={props.submitting || props.loading}
              label="Final publication date"
              value={get(props.values, 'finalDate', null)}
              onChange={(value) => props.onChange('finalDate', value)}
              errorText={get(props.errors, 'finalDate', null)}
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            <LocalFileInput
              required
              value={get(props.values, 'webImage', null)}
              onChange={(value) => props.onChange('webImage', value)}
              label="Web Image (1100x200)"
              accept="image"
              disabled={props.submitting || props.loading}
              errorText={get(props.errors, 'webImage', null)}
            />
          </Column>
          <Column size={4}>
            <LocalFileInput
              required
              value={get(props.values, 'mobileImage', null)}
              onChange={(value) => props.onChange('mobileImage', value)}
              label="Mobile Image (400x250)"
              accept="image"
              disabled={props.submitting || props.loading}
              errorText={get(props.errors, 'mobileImage', null)}
            />
          </Column>
        </Row>
      </Card>
      <Separator />
      <FormButtons
        confirmLabel={`${isEditing ? 'Update' : 'Create'} Banner`}
        isDisabled={props.submitting || props.loading}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
  );
};

BannerForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  values: PropTypes.object,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  loading: PropTypes.bool,
};

BannerForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  loading: false,
};

export default BannerForm;
